import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from './hrApi';
import { useAuth } from './hrAuth';
import EmployeeSelect from './hrComponents/EmployeeSelect';

const MON = ["January","February","March","April","May","June","July",
             "August","September","October","November","December"];
const DOWS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
const tp = s => { const m = String(s||'').match(/\d{1,2}:\d{2}\s*[AP]M/i); return m ? m[0] : (s||'-'); };
const hm2m = s => { const m = String(s||'').match(/^(\d+):(\d{2})$/); return m ? (+m[1])*60+(+m[2]) : 0; };
const m2hm = n => `${String(Math.floor(n/60)).padStart(2,'0')}:${String(n%60).padStart(2,'0')}`;
const today = () => { const d=new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; };

function stCls(st) {
  const s = (st||'').toLowerCase();
  if (s.includes('present')) return 'st-present';
  if (s.includes('absent')) return 'st-absent';
  if (s.includes('half')||s.includes('partial')) return 'st-partial';
  if (s.includes('week')||s.includes('holiday')||s.includes('off')||s.includes('leave')) return 'st-off';
  return '';
}
function dayCls(st) {
  const s = (st||'').toLowerCase();
  if (s.includes('present')) return 'd-present';
  if (s.includes('absent')) return 'd-absent';
  if (s.includes('half')||s.includes('partial')) return 'd-partial';
  if (s.includes('week')||s.includes('holiday')||s.includes('off')||s.includes('leave')) return 'd-offday';
  return '';
}

function weeksOf(y, m) {
  const ndays = new Date(y, m+1, 0).getDate();
  const weeks = []; let cur = [];
  for (let d = 1; d <= ndays; d++) {
    if (new Date(y, m, d).getDay() === 0 && cur.length) { weeks.push(cur); cur = []; }
    cur.push(d);
  }
  if (cur.length) weeks.push(cur);
  return weeks;
}

export default function Attendance() {
  const { has, user } = useAuth();
  const [searchParams] = useSearchParams();
  const now = new Date();
  const [y, setY] = useState(now.getFullYear());
  const [m, setM] = useState(now.getMonth());
  const [view, setView] = useState('cal');
  const [emp, setEmp] = useState(searchParams.get('emp') || '');
  const [days, setDays] = useState({});
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const showEmpSel = user.role === 'admin' || has('team');

  const mk = `${y}-${String(m+1).padStart(2,'0')}`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api(`/attendance?month=${mk}${emp ? '&emp='+emp : ''}`);
      setDays(d.days || {});
    } catch { setDays({}); }
    setLoading(false);
  }, [mk, emp]);

  useEffect(() => { load(); }, [load]);

  const move = (d) => {
    let nm = m + d;
    if (nm < 0) { setM(11); setY(y-1); }
    else if (nm > 11) { setM(0); setY(y+1); }
    else setM(nm);
  };

  const ndays = new Date(y, m+1, 0).getDate();
  const first = new Date(y, m, 1).getDay();
  const t = today();

  return (
    <section className="page on">
      <h2 className="pagetitle display">Attendance</h2>
      <div className="pagesub">Day-wise records from Zoho People.</div>
      {showEmpSel && <EmployeeSelect value={emp} onChange={v => { setEmp(v); }} />}
      <div className="cal-head">
        <button className="nav-btn" onClick={() => move(-1)}>‹</button>
        <div className="mon display">{MON[m]} {y}</div>
        <button className="nav-btn" onClick={() => move(1)}>›</button>
        <div className="viewseg">
          {['cal','tab','week'].map(v =>
            <button key={v} className={view===v?'sel':''} onClick={() => setView(v)}>
              {v==='cal'?'Calendar':v==='tab'?'Table':'Weekly'}
            </button>
          )}
        </div>
      </div>

      {loading ? <div className="loading">Loading attendance…</div> :
       view === 'cal' ? (
        <>
          <div className="cal">
            {["SUN","MON","TUE","WED","THU","FRI","SAT"].map(d => <div key={d} className="dow">{d}</div>)}
            {Array.from({length:first}).map((_,i) => <div key={'e'+i} className="day empty" />)}
            {Array.from({length:ndays}).map((_,i) => {
              const d = i+1;
              const ds = `${mk}-${String(d).padStart(2,'0')}`;
              const info = days[ds];
              return (
                <div key={d} className={`day ${info?dayCls(info.status):''} ${ds===t?'today':''} ${detail===ds?'sel':''}`}
                     onClick={() => setDetail(ds)}>
                  <div className="n">{d}</div>
                  <div className="st">{info?.status||''}</div>
                </div>
              );
            })}
          </div>
          {detail && (
            <div className="detail on">
              <h3>{detail}</h3>
              {days[detail] ? Object.entries({
                Status: days[detail].status, 'First In': days[detail].first_in,
                'Last Out': days[detail].last_out, 'Total Hours': days[detail].total_hours,
                Overtime: days[detail].overtime, Shift: days[detail].shift,
              }).map(([k,v]) => <div key={k} className="drow"><span className="k">{k}</span><span>{v||'-'}</span></div>)
                : <div className="drow"><span className="k">No record</span><span>—</span></div>}
            </div>
          )}
        </>
       ) : view === 'tab' ? (
        <table className="t">
          <thead><tr><th>DATE</th><th>DAY</th><th>STATUS</th><th>FIRST IN</th><th>LAST OUT</th><th>TOTAL HRS</th><th>OT</th><th>SHIFT</th></tr></thead>
          <tbody>
            {weeksOf(y,m).map((week,wi) => {
              let wTot = 0;
              const rows = week.map(d => {
                const ds = `${mk}-${String(d).padStart(2,'0')}`;
                const i = days[ds];
                if (i) wTot += hm2m(i.total_hours);
                return (
                  <tr key={d} className={ds===t?'todayrow':''}>
                    <td>{ds}</td><td>{DOWS[new Date(y,m,d).getDay()]}</td>
                    <td className={i?stCls(i.status):''}>{i?.status||'—'}</td>
                    <td>{i?tp(i.first_in):'—'}</td><td>{i?tp(i.last_out):'—'}</td>
                    <td>{i?.total_hours||'—'}</td><td>{i?.overtime||'—'}</td><td>{i?.shift||'—'}</td>
                  </tr>
                );
              });
              return [
                <tr key={'w'+wi} className="weekrow"><td colSpan={8}>Week {wi+1} · {m2hm(wTot)} hrs</td></tr>,
                ...rows
              ];
            })}
          </tbody>
        </table>
       ) : (
        <table className="t">
          <thead><tr><th>WEEK</th><th>RANGE</th><th>PRESENT</th><th>ABSENT</th><th>OFF/LEAVE</th><th>TOTAL HRS</th><th>OT</th></tr></thead>
          <tbody>
            {weeksOf(y,m).map((week,wi) => {
              let pres=0,abs=0,off=0,tot=0,ot=0;
              week.forEach(d => {
                const i = days[`${mk}-${String(d).padStart(2,'0')}`];
                if (!i) return;
                const s = (i.status||'').toLowerCase();
                if (s.includes('present')) pres++;
                else if (s.includes('absent')) abs++;
                else if (s) off++;
                tot += hm2m(i.total_hours); ot += hm2m(i.overtime);
              });
              return (
                <tr key={wi}>
                  <td>Week {wi+1}</td>
                  <td>{mk}-{String(week[0]).padStart(2,'0')} → {mk}-{String(week[week.length-1]).padStart(2,'0')}</td>
                  <td className="st-present">{pres}</td><td className={abs?'st-absent':''}>{abs}</td>
                  <td>{off}</td><td>{m2hm(tot)}</td><td>{m2hm(ot)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
       )}
    </section>
  );
}
